import { useState } from 'react';
import { Check, ChevronDown, MessageSquare, Pencil, Plus, X } from 'lucide-react';
import type { AiChatBase } from '../../hooks/useAiChat';
import { useI18n } from '../../i18n';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

/** The same conversation picker in Studio and the table's assistant. */
export function ConversationHistory({ chat, inline = false }: { chat: AiChatBase; inline?: boolean }) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const currentTitle = chat.conversationTitle ?? chat.conversations.find(c => c.id === chat.conversationId)?.title ?? t('ai.newChat');
  const run = async (action: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    try { await action(); }
    catch (e) { setError(e instanceof Error ? e.message : t('ai.historyLoadError')); }
    finally { setBusy(false); }
  };
  const blocked = busy || chat.isLoadingHistory || chat.isStreaming;
  const save = () => run(async () => {
    if (!editing || !title.trim() || !chat.renameConversation) return;
    await chat.renameConversation(editing, title.trim());
    setEditing(null);
  });
  return (
    <div className={inline ? 'flex min-w-0 flex-1 items-center gap-0.5' : 'flex min-w-0 shrink-0 items-center gap-1 border-b border-line px-3 py-1.5'} data-testid="conversation-bar">
      <Popover open={open} onOpenChange={value => {
        setOpen(value);
        if (value) { setEditing(null); void run(() => chat.loadConversations()); }
      }}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="min-w-0 flex-1 justify-start gap-2 px-1" aria-label={t('ai.conversationHistory')} title={t('ai.conversationHistory')}>
            <MessageSquare size={14} className="shrink-0 text-ai" />
            <span className="truncate text-ui">{currentTitle}</span>
            <ChevronDown size={13} className="ml-auto shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" collisionPadding={12} className="w-[360px] max-w-[calc(100vw-24px)] border-line p-0" aria-label={t('ai.conversationHistory')}>
          <div className="border-b border-line px-3 py-2">
            <p className="text-ui font-medium">{t('ai.conversationHistory')}</p>
            <p className="mt-1 text-meta text-dim">{t('ai.historyRetention')}</p>
          </div>
          <div className="max-h-[min(400px,55vh)] overflow-y-auto p-1">
            {chat.conversations.map(conv => (
              <div key={conv.id} className={`mb-1 rounded-md border ${conv.id === chat.conversationId ? 'border-ai/40 bg-ai/10' : 'border-transparent hover:bg-panel-2'}`}>
                {editing === conv.id ? (
                  <form className="flex items-center gap-1 p-2" onSubmit={e => { e.preventDefault(); void save(); }}>
                    <Input autoFocus value={title} maxLength={100} aria-label={t('ai.historyTitle')} onChange={e => setTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); } }} />
                    <Button type="submit" size="icon" variant="ghost" disabled={busy || !title.trim()} aria-label={t('ai.historySave')}><Check size={14} /></Button>
                    <Button type="button" size="icon" variant="ghost" disabled={busy} onClick={() => setEditing(null)} aria-label={t('ai.historyCancel')}><X size={14} /></Button>
                  </form>
                ) : (
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" className="h-auto min-w-0 flex-1 flex-col items-start gap-1 whitespace-normal px-2 py-2 text-left" disabled={blocked} aria-current={conv.id === chat.conversationId ? 'true' : undefined} onClick={() => void run(async () => { await chat.loadConversation(conv.id); setOpen(false); })}>
                      <span className="w-full truncate text-ui">{conv.title || t('ai.newChat')}</span>
                      <span className="text-meta text-dim">{new Date(conv.updatedAt).toLocaleDateString(locale === 'ru' ? 'ru-RU' : 'en-US', { day: 'numeric', month: 'short' })}{conv.id === chat.conversationId ? ` · ${t('ai.historyCurrent')}` : ''}</span>
                    </Button>
                    {chat.renameConversation && <Button variant="ghost" size="icon" className="mr-1 shrink-0" disabled={busy} aria-label={t('ai.historyRename').replace('{title}', conv.title || t('ai.newChat'))} onClick={() => { setTitle(conv.title ?? ''); setEditing(conv.id); setError(null); }}><Pencil size={13} /></Button>}
                  </div>
                )}
              </div>
            ))}
            {chat.isLoadingHistory ? <p className="p-3 text-ui text-dim" role="status">{t('ai.loadingHistory')}</p> : chat.conversations.length === 0 && !error ? <p className="p-3 text-ui text-dim">{t('ai.noConversations')}</p> : null}
            {(chat.conversationsTotal ?? 0) > chat.conversations.length && <Button variant="ghost" className="w-full" disabled={busy || chat.isLoadingHistory} onClick={() => void run(() => chat.loadConversations(true))}>{t('ai.historyMore')}</Button>}
          </div>
          {chat.isStreaming && <p className="px-3 py-2 text-meta text-dim">{t('ai.historyBusy')}</p>}
          {error && <div className="border-t border-line p-3 text-ui text-danger" role="alert">{error}<Button variant="ghost" size="sm" disabled={busy} onClick={() => void run(() => chat.loadConversations())}>{t('ai.historyRetry')}</Button></div>}
        </PopoverContent>
      </Popover>
      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" disabled={blocked} aria-label={t('ai.newChat')} title={t('ai.newChat')} onClick={() => { chat.resetChat(); setOpen(false); }}><Plus size={14} /></Button>
    </div>
  );
}
